import { createContext, useContext, useMemo } from 'react';
import { useFormatLocale } from '../formatting';
import { createIntlComparator } from './createIntlComparator';
import { addDefaults } from './types';

/**
 * A context that provides a common Comparator for all components.
 */
const ComparatorContext = /*#__PURE__*/createContext(undefined);
export const ComparatorProvider = ComparatorContext.Provider;

/**
 * Unless there is a specific use case for this hook, consider using `useComparator` instead.
 *
 * Returns a factory function creating comparators. Can be used to directly interact with a
 * provided comparator factory or manually compose custom comparator functions.
 *
 * @returns a factory functions for creating comparators.
 */
export const useComparatorFactory = () => {
  return useContext(ComparatorContext) ?? createIntlComparator;
};

/**
 * Create a comparator function for sorting. Use the returned comparator with Array.prototype.sort().
 *
 * NOTE: Array.prototype.sort() uses an in-place sorting algorithm. This can relevant to React's
 * change detection. React won't detect changes to in-place sorts. A new array must be provided.
 * Using the spread operator makes it easy to first copy the array then sort the copy. The new array
 * is a change that React can detect.
 *
 * ```
 * const comparator = useComparator(options);
 * const sortedArray = [...unsortedArray].sort();
 * ```
 *
 * If no comparator factory is provided, the hook will use a wrapper around Intl.Comparator. If you need
 * need the collation functionality of the SAS portable collator you will need to provide an LTJS comparator
 * factory to `AppRoot`, `NovaProviders`, or directly to `ComparatorContext`.
 *
 * The comparator function is memoized based on the comparator factory and options passed in. To keep the same
 * reference to the comparator, callers must provide the same `options` object. Otherwise you will get a new function
 * every time and any effects, callbacks, or memoized values based on it will rerun on every render.
 *
 * @param options
 * @returns a memoized comparator function that can be used for sorting
 */
export const useComparator = options => {
  const locale = useFormatLocale();
  const comparatorFactory = useComparatorFactory();
  const comparator = useMemo(() => {
    if (Array.isArray(options)) {
      const comparators = options.map(o => comparatorFactory(locale, addDefaults(o)));
      return createMultiComparator(comparators);
    }
    return comparatorFactory(locale, addDefaults(options));
  }, [comparatorFactory, locale, options]);
  return comparator;
};

/**
 * @param comparators
 * @returns a single comparator that checks each comparator in order for a result
 */
export const createMultiComparator = comparators => {
  return (a, b) => {
    return comparators.reduce((result, comparator) => {
      return result === 0 ? comparator(a, b) : result;
    }, 0);
  };
};
//# sourceMappingURL=useComparator.js.map